import { createGlobalStyle, css, styled } from "styled-components";

import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import type { LanguageDirectionType } from "@ubnt/store.core/lib/contexts/MenuContext/utils";
import { Badge as BadgeBase } from "@ubnt/store.core/lib/components/Badge";
import { Button as ButtonBase } from "@ubnt/store.core/lib/components/Button";
import { StyleableSvgImage } from "@ubnt/store.core/lib/components/StyleableSvgImage";
import { StickyGridItem } from "./CompareTableMarkup/components/StickyGrid";

export const MOBILE_BREAKPOINT = 650;

export const TOOLTIP_CLASS_NAME = `model-compare-tooltip`;
export const PRODUCT_HEADER_TOOLTIP_CLASSNAME =
  "model-compare-product-header-tooltip";

export const PlaceholderGridItem = styled(StickyGridItem)`
  background: ${({ theme }) => theme.colors.neutral00};
  z-index: 3;
`;

export const CenterPlaceholderGridItem = styled(StickyGridItem)``;

export const HEADER_GRID_ITEM_Y_PADDING = 16;

export const HEADER_GRID_ITEM_GAP_DESKTOP = 24;
export const HEADER_GRID_ITEM_GAP_MOBILE = 12;

export const ProductHeaderTooltipStyles = createGlobalStyle`
  .${PRODUCT_HEADER_TOOLTIP_CLASSNAME} {
    box-shadow: 0 8px 24px 0 #00000014;
    width: 175px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    padding: 10px 20px;
    margin-top: -1.5px;
    background: white;
    text-align: center;

    &::before {
      box-shadow: 0 8px 24px 0 #00000014;
      background: white;
    }
  }
`;

// This expands the hoverable area of the tooltip so users can move their mouse casually to the tooltip
export const ProductHeaderTooltipHoverArea = styled.div<{
  $width: number | undefined;
  $tooltipIsPositionedTop: boolean;
}>`
  position: absolute;
  ${({ $tooltipIsPositionedTop }) =>
    $tooltipIsPositionedTop
      ? css`
          bottom: 0;
        `
      : css`
          top: 0;
        `}
  bottom: 0;
  right: 50%;
  transform: translateX(50%);
  width: ${({ $width }) => $width ?? 175}px;
  height: 40px;
`;

export const ProductHeaderTooltipLink = styled.a`
  color: ${({ theme }) => theme.colors.ublue06};

  &:hover {
    color: ${({ theme }) => theme.colors.ublue06};
  }
`;

export const HeaderGridItem = styled(StickyGridItem)`
  background: ${({ theme }) => theme.colors.neutral00};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: ${HEADER_GRID_ITEM_Y_PADDING}px 0;
  gap: ${HEADER_GRID_ITEM_GAP_DESKTOP}px;
  position: relative;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    gap: ${HEADER_GRID_ITEM_GAP_MOBILE}px;
    /* Need this padding-top so sticky offset looks good on mobile */
    padding: 25px 0 0;
  }
`;

export const EmptyHeaderGridItem = styled(HeaderGridItem)`
  align-items: center;
  justify-content: center;
`;

export const EmptyHeaderEmptyProductDescription = styled.div<{
  $headerItemsAreSticking: boolean;
}>`
  width: 100%;
  height: 100%;

  ${({ $headerItemsAreSticking }) =>
    $headerItemsAreSticking &&
    css`
      display: none;
    `}
`;

export const HeaderGridItemContainer = styled.div<{ $isStuck: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const RemoveProductButton = styled.button<{ $isSmallScreen: boolean }>`
  position: absolute;
  top: 25px;
  right: 25px;
  background: transparent;
  border: none;
  padding: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 30px;
  color: ${({ theme }) => theme.colors.neutral06};

  ${({ $isSmallScreen }) =>
    $isSmallScreen &&
    css`
      top: 0;
      right: 4%;
    `}
`;

export const ProductMeta = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  width: 100%;
  padding: 0 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    &::before {
      display: none;
    }
    &::after {
      display: none;
    }
  }
`;

const productTitleStyles = css`
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.32px;
`;

export const ProductTitle = styled.div`
  ${productTitleStyles}

  color: ${({ theme }) => theme.colors.text1};

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 14px;
  }
`;

export const ProductTitleLink = styled.button`
  ${productTitleStyles}
  color: ${({ theme }) => theme.colors.ublue06};
  border: none;
  background: transparent;

  :focus,
  :hover {
    text-decoration: none;
  }

  :focus {
    color: ${({ theme }) => theme.colors.ublue06};
  }
`;

export const ProductDescription = styled.div<{
  $maxWidth?: number;
  $direction: LanguageDirectionType;
}>`
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  margin-top: 6px;
  letter-spacing: -0.24px;
  color: ${({ theme }) => theme.colors.text2};
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : "auto")};
  direction: ${({ $direction }) => $direction && `${$direction}`};

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    margin: 4px auto 12px;
    font-size: 12px;
  }
`;

export const SelectFromAboveIsStuckContainer = styled.div<{
  $isStuck: boolean;
}>`
  display: none;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;

  ${({ $isStuck }) =>
    $isStuck &&
    css`
      display: flex;
    `}
`;

export const SelectFromAboveIsStuck = styled(ProductDescription)`
  margin: 0;
`;

export const ProductNote = styled.div`
  color: ${({ theme }) => theme.colors.text3};
  font-size: ${({ theme }) => theme.font.size10};
  line-height: 16.8px;
  margin-top: 6px;
  text-align: center;
`;

export const Badge = styled(BadgeBase)`
  margin: 10px 0 20px;
`;

export const BadgeWrapper = styled.div`
  margin-top: auto;
  display: flex;
  gap: 10px;
`;

export const PropertyTitleGridItem = styled(StickyGridItem)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral03};
  background: ${({ theme }) => theme.colors.neutral00};
  z-index: 1;
  overflow: hidden;
`;

export const EmptyPropertyTitleGridItem = styled(StickyGridItem)`
  background: ${({ theme }) => theme.colors.neutral00};
`;

export const PropertyTitleGridItemContent = styled(StickyGridItem)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 16px 16px 8px;
  width: 100%;
  gap: 8px;
  overflow: hidden;
`;

export const PropertyTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text2};
  line-height: 16.8px;
  letter-spacing: -0.28px;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 12px;
    line-height: 14px;
  }
`;

export const PropertyIcon = styled.div`
  width: 30px;
  height: 30px;
  color: ${({ theme }) => theme.colors.text1};
`;

export const SelectFromAboveIconCopyContainer = styled.div<{
  $isStuck: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 1;

  ${({ $isStuck }) =>
    $isStuck &&
    css`
      opacity: 0;
    `}
`;

export const ProductImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  -webkit-user-drag: none;
`;

export const ProductImageWrap = styled.div<{ height: number }>`
  height: ${({ height }) => `${height}px`};
  display: flex;
`;

export const PropertyTooltipHoverArea = styled.div`
  position: absolute;
  height: 35px;
  width: 35px;
  top: 50%;
  transform: translateY(-50%);
`;

export const PropertyTooltip = styled.div`
  position: relative;
  height: 20px;
`;

export const PropertyTooltipContent = styled.div`
  color: ${({ theme }) => theme.colors.text1};
  font-size: 12px;

  a {
    color: ${({ theme }) => theme.colors.ublue06};
  }
`;

export const TooltipIcon = styled(StyleableSvgImage)`
  color: ${({ theme }) => theme.colors.ublue06};
`;

export const ButtonWrap = styled.div`
  padding: 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const buttonStyles = css`
  padding: 10px 15px;
  line-height: 20px;
  font-weight: 700;
  margin: auto;
  :focus,
  :hover {
    text-decoration: none;
  }

  > * {
    position: static;
  }
`;

export const Button = styled(ButtonBase)<{ $removePointerEvents: boolean }>`
  ${buttonStyles};

  ${({ $removePointerEvents }) =>
    $removePointerEvents &&
    css`
      pointer-events: none;
    `}
`;

// TODO: remove if not used anywhere else in store.core
export const ComingSoon = styled(ButtonBase)`
  ${buttonStyles};

  cursor: default;
  color: #445178;

  :focus,
  :hover {
    color: #445178;
  }
`;

export const NotesWrap = styled.div`
  padding: 16px 10px 10px;
  text-wrap: pretty;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Note = styled.div`
  font-size: 10px;
  line-height: ${17 / 10};
  color: ${({ theme }) => theme.colors.text3};
  text-align: center;
`;

export const FootnotesWrap = styled.div`
  padding: 0 0 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Footnote = styled.div`
  font-size: 10px;
  line-height: ${17 / 10};
  color: ${({ theme }) => theme.colors.text3};
  text-align: center;
`;

export const KitIncluded = styled.div`
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.28px;
  line-height: 16.8px;
  color: ${({ theme }) => theme.colors.text3};
`;

export const TooltipStyles = createGlobalStyle<{ maxWidth?: number }>`
  .${TOOLTIP_CLASS_NAME} {
    box-shadow: 0 8px 24px 0 #00000014;
    max-width: ${({ maxWidth }) => maxWidth ?? 200}px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    padding: 8px 16px;
    margin-top: -1.5px;
    background: ${({ theme }) => theme.colors.neutral00};

    &::before {
      box-shadow: 0 8px 24px 0 #00000014;
      background: ${({ theme }) => theme.colors.neutral00};
    }
  }
`;
